import type { ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useHasResolvedOnce } from "@/hooks/useHasResolvedOnce";
import { BrandedLoadingSpinner } from "@/components/brand";

/**
 * One URL, two audiences. /organization/:slug (and its article route) serves
 * the full, session-gated experience to a signed-in user and the public,
 * crawlable page to everyone else — no redirect to sign-in, which is what
 * kept every crawler out.
 *
 * `authenticated` is expected to carry its own ProtectedRoute, so the
 * signed-in branch keeps every existing gate; this component only decides
 * which branch applies. Until the session has resolved once it shows the
 * spinner rather than the public page, so a signed-in user never sees the
 * public layout flash before their own view mounts. Crawlers that execute
 * JavaScript wait for network idle and see the resolved branch.
 */
const OrganizationRouteSwitch = ({ publicElement, authenticated }: { publicElement: ReactNode; authenticated: ReactNode }) => {
  const { user, isLoading } = useAuth();
  const hasResolvedOnce = useHasResolvedOnce(!isLoading);

  if (isLoading && !hasResolvedOnce) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <BrandedLoadingSpinner size="lg" />
      </div>
    );
  }

  return <>{user ? authenticated : publicElement}</>;
};

export default OrganizationRouteSwitch;
